import { Chip } from './atoms';
import {
  championStrengthMeta,
  ynMeta,
  type PipelineAccount,
} from '@/lib/pipelineAdapter';

export function EBCell({
  eb,
  signedOff,
}: {
  eb: PipelineAccount['economicBuyer'];
  signedOff: PipelineAccount['ebSignedOff'];
}) {
  if (!eb.value) {
    return (
      <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
        — Not identified
      </span>
    );
  }
  const conf = eb.confidence;
  const color =
    conf === 'confirmed' ? '#047857' : conf === 'tentative' ? '#a16207' : '#94a3b8';
  const bg =
    conf === 'confirmed' ? '#ecfdf5' : conf === 'tentative' ? '#fefce8' : '#f1f5f9';
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: '#0f172a',
          lineHeight: 1.35,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {eb.value}
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
        <Chip size="sm" bg={bg} color={color} dot={color}>
          {conf}
        </Chip>
        {signedOff === 'yes' && (
          <Chip size="sm" bg="#ecfdf5" color="#047857">
            signed
          </Chip>
        )}
        {signedOff === 'partial' && (
          <Chip size="sm" bg="#fefce8" color="#a16207">
            partial sign
          </Chip>
        )}
      </div>
    </div>
  );
}

export function ChampCell({ c }: { c: PipelineAccount['champion'] }) {
  if (!c.name) {
    return (
      <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
        — Missing
      </span>
    );
  }
  const m = championStrengthMeta[c.strength];
  return (
    <div>
      <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>{c.name}</div>
      {c.role && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{c.role}</div>
      )}
      <Chip size="sm" bg={m.bg} color={m.fg} dot={m.dot} style={{ marginTop: 4 }}>
        {m.label}
      </Chip>
    </div>
  );
}

export function YnPill({ value }: { value: PipelineAccount['budgetApproved']['value'] }) {
  const m = ynMeta[value];
  return (
    <Chip bg={m.bg} color={m.fg} dot={m.dot} size="sm">
      {m.label}
    </Chip>
  );
}

export function LaunchCell({ ld }: { ld: PipelineAccount['launchDateAgreed'] }) {
  if (!ld.value) {
    return (
      <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
        — Unset
      </span>
    );
  }
  const d = new Date(ld.value);
  const fmt = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const yr = d.getFullYear();
  return (
    <div>
      <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>
        {fmt}{' '}
        <span style={{ color: '#94a3b8', fontWeight: 400 }}>{yr}</span>
      </div>
      <Chip
        size="sm"
        bg={ld.status === 'agreed' ? '#ecfdf5' : '#fefce8'}
        color={ld.status === 'agreed' ? '#047857' : '#a16207'}
        style={{ marginTop: 3 }}
      >
        {ld.status}
      </Chip>
    </div>
  );
}

interface BlockersCellProps {
  blockers: string[];
  expand: boolean;
}

export function BlockersCell({ blockers, expand }: BlockersCellProps) {
  if (!blockers.length) {
    return <span style={{ fontSize: 12, color: '#94a3b8' }}>None</span>;
  }
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span
          style={{
            background: '#fef2f2',
            color: '#b91c1c',
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 6px',
            borderRadius: 4,
            flexShrink: 0,
          }}
        >
          {blockers.length}
        </span>
        <span
          style={{
            fontSize: 12,
            color: '#475569',
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {blockers[0]}
        </span>
      </div>
      {expand && blockers.length > 1 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 6,
            zIndex: 50,
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '10px 12px',
            boxShadow: '0 8px 24px -8px rgba(15,23,42,0.18)',
            width: 320,
          }}
        >
          {blockers.map((b, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: i < blockers.length - 1 ? 8 : 0,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  background: '#ef4444',
                  marginTop: 7,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 12, color: '#334155', lineHeight: 1.45 }}>{b}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
